import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { BUILD_TIME } from "../buildInfo";

const CHECK_INTERVAL = 60000; // alle 60 s pruefen
const RELOAD_DELAY = 6; // Sekunden bis zum automatischen Neuladen

// Erkennt automatisch eine neue veroeffentlichte Version (vergleicht die beim Build
// eingebackene BUILD_TIME mit /version.json) und laedt die PWA nach kurzem Hinweis neu.
export const UpdateBanner = () => {
  const [show, setShow] = useState(false);
  const [count, setCount] = useState(RELOAD_DELAY);
  const bootRef = useRef(BUILD_TIME);
  const tickRef = useRef(null);

  const doReload = useCallback(() => {
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then((r) => r && r.update().catch(() => {}));
      }
    } catch {
      /* ignore */
    }
    window.location.reload();
  }, []);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.build && bootRef.current && data.build !== bootRef.current) {
        setShow(true);
      }
    } catch {
      /* offline o. Ae. – ignorieren */
    }
  }, []);

  useEffect(() => {
    check();
    const iv = setInterval(check, CHECK_INTERVAL);
    const onWake = () => check();
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [check]);

  useEffect(() => {
    if (!show) return undefined;
    setCount(RELOAD_DELAY);
    tickRef.current = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          clearInterval(tickRef.current);
          doReload();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [show, doReload]);

  if (!show) return null;

  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom)+12px)]"
    >
      <div
        className="flex w-full max-w-md items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl"
        style={{ background: "#0F172A", color: "#F8FAFC", border: "1px solid #1E293B" }}
      >
        <RefreshCw size={20} className="shrink-0 animate-spin" style={{ color: "#34D399" }} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Neue Version verfügbar</p>
          <p className="text-xs leading-tight" style={{ color: "#94A3B8" }}>
            Wird in {count} s automatisch aktualisiert …
          </p>
        </div>
        <button
          type="button"
          data-testid="update-reload-btn"
          onClick={doReload}
          className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
          style={{ background: "#34D399", color: "#06281C" }}
        >
          Jetzt
        </button>
      </div>
    </div>
  );
};
